import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductDetail } from '@/lib/api';
import type { JSX, KeyboardEvent } from 'react';

/**
 * Adams ships 2 to 14 images (mode 4), Artisan 8 to 45 (mode 11, one product has 45). A
 * carousel tuned for 4 fails at 45 and a thumbnail grid tuned for 45 looks empty at 2, so
 * the layout switches on count. Everything past the first two is lazy-loaded.
 */
const DENSE_THRESHOLD = 8;

export function Gallery({
  images,
  name,
  selectionKey,
}: {
  images: ProductDetail['images'];
  name: string;
  /** Changing the variant crossfades the main image. */
  selectionKey: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const stripRef = useRef<HTMLDivElement>(null);
  const dense = images.length >= DENSE_THRESHOLD;

  useEffect(() => {
    setActive(0);
    setLoaded(false);
  }, [selectionKey]);

  // Fade the new photograph in, but never gate its visibility on the transition running.
  useEffect(() => {
    setLoaded(false);
  }, [active]);

  if (images.length === 0) {
    return (
      <div
        className="flex aspect-square items-center justify-center bg-[var(--color-ground-lift)] text-xs text-[var(--color-muted)]"
        role="img"
        aria-label={name}
      >
        {name}
      </div>
    );
  }

  const current = images[active] ?? images[0];

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowRight') setActive((i) => Math.min(images.length - 1, i + 1));
    if (event.key === 'ArrowLeft') setActive((i) => Math.max(0, i - 1));
  };

  // `min-w-0` matters: a grid item defaults to `min-width: auto`, so the thumbnail strip's
  // intrinsic width would force the whole page 2784px wide and `overflow-x-auto` would
  // never get the chance to clip.
  return (
    <div className="min-w-0 space-y-3" data-testid="gallery" data-image-count={images.length}>
      <div
        className="relative overflow-hidden bg-[var(--color-ground-lift)] aspect-square"
        tabIndex={0}
        role="group"
        aria-roledescription="carousel"
        aria-label={t('product.gallery')}
        onKeyDown={onKeyDown}
      >
        {/*
          A plain <img> whose `src` changes, with the crossfade done in CSS.
          Framer's AnimatePresence was doing this, but an animation that does not run —
          a backgrounded tab, a throttled renderer — left the incoming image stuck at
          `opacity: 0`, i.e. a blank gallery. The image must never depend on an animation
          frame to become visible.
        */}
        <img
          src={current?.full ?? current?.card ?? ''}
          srcSet={
            [current?.card ? `${current.card} 640w` : '', current?.full ? `${current.full} 1600w` : '']
              .filter(Boolean)
              .join(', ') || undefined
          }
          sizes="(max-width: 1024px) 100vw, 55vw"
          alt={current?.alt ?? name}
          width={current?.width ?? 1200}
          height={current?.height ?? 1200}
          loading="eager"
          decoding="async"
          onLoad={() => setLoaded(true)}
          className="h-full w-full object-contain p-4 transition-opacity duration-300 motion-reduce:transition-none"
          style={{ opacity: loaded ? 1 : 0.4 }}
        />

        <p className="absolute bottom-3 right-3 bg-[var(--color-ground-lift)]/90 px-2 py-1 text-2xs font-semibold tabular-nums">
          {t('product.imageOf', { index: active + 1, total: images.length })}
        </p>
      </div>

      {images.length > 1 && (
        <div
          ref={stripRef}
          /* Sparse: a comfortable row. Dense: a scrolling strip that does not swamp the page. */
          className={
            dense
              ? 'flex min-w-0 gap-1.5 overflow-x-auto pb-1 [scrollbar-width:thin]'
              : 'grid grid-cols-4 gap-1.5 sm:grid-cols-6'
          }
        >
          {images.map((image, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setActive(index)}
              aria-label={t('product.imageOf', { index: index + 1, total: images.length })}
              aria-current={index === active ? 'true' : undefined}
              className={dense ? 'h-16 w-14 shrink-0' : 'aspect-square'}
              style={{ boxShadow: index === active ? 'inset 0 0 0 2px var(--color-brand)' : undefined }}
            >
              <img
                src={image.thumb ?? image.card ?? ''}
                alt=""
                width={120}
                height={150}
                loading={index < 2 ? 'eager' : 'lazy'}
                decoding="async"
                className="h-full w-full bg-[var(--color-ground-lift)] object-contain p-0.5"
                style={{ opacity: index === active ? 1 : 0.65 }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
